import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, CheckCircle2, XCircle, ChevronDown, Beaker, Layers, Droplets } from 'lucide-react';
import type { AssessmentResult } from '../hooks/useCalculator';

interface FiltrationSectionProps {
  result: AssessmentResult;
}

export default function FiltrationSection({ result }: FiltrationSectionProps) {
  const [activeFilter, setActiveFilter] = useState<'mmf' | 'pmf' | 'gac'>('mmf');

  const mmfScore = result.processCompatibility.find(p => p.process === 'Multimedia Filter (MMF)');
  const pmfScore = result.processCompatibility.find(p => p.process === 'Pressure Multimedia Filter');
  const gacScore = result.processCompatibility.find(p => p.process === 'GAC Adsorption');

  const filters = [
    {
      key: 'mmf' as const,
      name: 'Multimedia Filter (MMF)',
      data: mmfScore,
      media: ['Anthracite (top layer)', 'Silica Sand (middle)', 'Gravel (support)'],
      typicalRemoval: [
        { param: 'TSS', range: '85-95%', detail: 'Down to 5-10 mg/L' },
        { param: 'BOD', range: '15-25%', detail: 'Via solids capture' },
        { param: 'COD', range: '10-20%', detail: 'Particulate COD only' },
        { param: 'Turbidity', range: '90-95%', detail: '< 1 NTU typical' },
      ],
      bestFor: 'Pre-treatment before biological systems, polishing after coagulation, high TSS wastewater',
      limitations: 'Cannot remove dissolved organics alone, requires backwash, ineffective for O&G > 30 mg/L',
      position: 'After coagulation/flocculation or DAF, before biological treatment or as pre-treatment',
    },
    {
      key: 'pmf' as const,
      name: 'Pressure Multimedia Filter',
      data: pmfScore,
      media: ['Anthracite', 'Silica Sand', 'Garnet (finest layer)'],
      typicalRemoval: [
        { param: 'TSS', range: '90-98%', detail: 'Down to 2-5 mg/L' },
        { param: 'BOD', range: '15-25%', detail: 'Via solids capture' },
        { param: 'COD', range: '10-20%', detail: 'Particulate COD only' },
        { param: 'Turbidity', range: '92-98%', detail: '< 0.5 NTU possible' },
      ],
      bestFor: 'Industrial high-flow applications, pre-treatment for membranes, space-constrained sites',
      limitations: 'Higher capital cost than gravity MMF, requires pressure vessel, not for O&G > 30 mg/L',
      position: 'After coagulation/flocculation, before RO/MBR/biological, or as standalone pre-treatment',
    },
    {
      key: 'gac' as const,
      name: 'GAC Adsorption',
      data: gacScore,
      media: ['Granular Activated Carbon (12x40 or 8x30 mesh)'],
      typicalRemoval: [
        { param: 'COD', range: '40-70%', detail: 'Dissolved refractory organics' },
        { param: 'BOD', range: '30-50%', detail: 'Biodegradable + refractory' },
        { param: 'Color', range: '80-95%', detail: 'True color removal' },
        { param: 'Phenol', range: '90-99%', detail: 'Trace organics' },
        { param: 'Taste/Odor', range: '85-99%', detail: 'Adsorption of compounds' },
      ],
      bestFor: 'Polishing after biological treatment, refractory organics (BOD/COD < 0.4), phenol removal, color removal, pre-treatment before membranes',
      limitations: 'High capital/operating cost, requires regeneration, not effective for high TSS (needs pre-filtration), capacity reduced by high TDS',
      position: 'After biological treatment (polishing), before membrane systems, or after MMF/Cloth Filter for dissolved organics',
    },
  ];

  const active = filters.find(f => f.key === activeFilter)!;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-500/20 border border-slate-500/20 flex items-center justify-center">
          <Filter className="w-5 h-5 text-slate-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white">Filtration & Adsorption Processes</h2>
          <p className="text-xs text-slate-500">Detailed analysis of MMF, PMF, and GAC process suitability</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {filters.map(f => {
          const isActive = activeFilter === f.key;
          const score = f.data?.compatibilityScore ?? 0;
          return (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`flex-1 p-3 rounded-xl border text-left transition-all ${
                isActive ? 'border-cyan-500/30 bg-cyan-500/10' : 'border-slate-800/50 bg-slate-800/30 hover:bg-slate-800/50'
              }`}
            >
              <div className="text-xs font-bold text-white mb-1">{f.name}</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div className={`h-full rounded-full ${score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-rb-brand' : 'bg-red-500'}`} initial={{ width: 0 }} animate={{ width: `${score}%` }} />
                </div>
                <span className="text-[10px] font-bold text-slate-400">{score}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active Filter Detail */}
      <motion.div key={active.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-white">{active.name}</h3>
            {active.data && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold inline-block mt-1 ${
                active.data.recommendation === 'RECOMMENDED' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' :
                active.data.recommendation === 'CONDITIONAL' ? 'text-rb-brand bg-rb-brand/10 border-rb-brand/20' :
                'text-red-400 bg-red-500/10 border-red-500/20'
              }`}>
                {active.data.recommendation} — Score: {active.data.compatibilityScore}
              </span>
            )}
          </div>
        </div>

        {/* Media */}
        <div className="space-y-2">
          <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Filter Media</h4>
          <div className="flex flex-wrap gap-2">
            {active.media.map((m, i) => (
              <span key={i} className="px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-xs text-slate-300">
                {m}
              </span>
            ))}
          </div>
        </div>

        {/* Removal Rates */}
        <div className="space-y-2">
          <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Typical Removal Performance</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {active.typicalRemoval.map((tr, i) => (
              <div key={i} className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
                <div className="text-[10px] text-slate-500 uppercase font-bold">{tr.param}</div>
                <div className="text-lg font-bold text-white">{tr.range}</div>
                <div className="text-[10px] text-slate-500">{tr.detail}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
            <div className="text-[10px] text-emerald-400 uppercase font-bold mb-1">Best For</div>
            <div className="text-xs text-slate-400">{active.bestFor}</div>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
            <div className="text-[10px] text-red-400 uppercase font-bold mb-1">Limitations</div>
            <div className="text-xs text-slate-400">{active.limitations}</div>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
            <div className="text-[10px] text-cyan-400 uppercase font-bold mb-1">Typical Position</div>
            <div className="text-xs text-slate-400">{active.position}</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
